"use client"

import { keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { Container, Typography, useMediaQuery, useTheme } from "@mui/material"
import Image from "next/image"
import "swiper/css"
import "swiper/css/pagination"
import { Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import RecommendationItem from "./RecommendationItem"

const StyledDiv = styled.div<{ $isGray?: boolean }>`
  padding-top: 40px;
  padding-bottom: 40px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ $isGray }) => ($isGray ? "#f3f4f6" : "transparent")};
  margin-top: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-top: unset;
    padding-top: 88px;
    padding-bottom: 88px;
  }
`

const StyledImageWrapper = styled.div`
  position: relative;
  height: 32px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 48px;
  }
`

// Regular container for fewer than 4 cards.
const StyledItemsDiv = styled(Container)`
  display: flex;
  /* flex-wrap: wrap; */
  justify-content: center;
  gap: 24px;
  padding-top: 64px;
`

// Keyframes for continuous horizontal scrolling.
// Animates from 0 (first set visible) to -50% (first set scrolled out).
const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

// AutoScrollDiv: For non‑mobile screens (4+ cards), duplicate items and animate.
const AutoScrollDiv = styled.div`
  overflow: hidden;
  width: 100%;
  padding-top: 64px;
  &:hover {
    .scrolling-content {
      animation-play-state: paused;
    }
  }
  .scrolling-content {
    display: inline-flex;
    gap: 24px;
    width: 200%; /* because items are duplicated */
    animation: ${scroll} 20s linear infinite;
  }
`

// MobileSwiper: Custom styled Swiper with pagination bullet styles.
const MobileSwiper = styled(Swiper)`
  padding-top: 24px !important;
  padding-bottom: 40px;
  padding-left: 16px;
  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background: #d1d5db;
    border-radius: 50%;
    margin: 0 4px;
  }
  .swiper-pagination-bullet-active {
    width: 8px;
    height: 8px;
    background: #1bbd77;
    border-radius: 50%;
  }
`

type Props = {
  data?: any
  recommendationsData?: any
  blockIndex?: number
  isHomepage?: boolean
}

const Recomendations = ({
  data,
  recommendationsData,
  blockIndex,
  isHomepage,
}: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  // Use recommendationsData from blockDataLoaders, or fall back to data prop
  const pageData = recommendationsData || data
  const image = extractProperty(pageData?.page, "image")
  const title = extractProperty(pageData?.page, "title")
  const itemsList = extractProperty(pageData?.page, "itemsList") || []
  // Duplicate items for a seamless loop on non-mobile screens
  const duplicatedItems = [...itemsList, ...itemsList]
  const isGray =
    isHomepage && typeof blockIndex === "number" && blockIndex % 2 === 1
  if (!pageData?.page || !itemsList?.length || !image || !title) {
    return null
  }
  return (
    <StyledDiv $isGray={isGray}>
      <StyledImageWrapper>
        <Image
          src={getUmbracoMediaUrl(image)}
          layout="fill"
          alt={title}
          style={{ objectFit: "cover" }}
        />
      </StyledImageWrapper>
      <Typography variant="h2" style={{ paddingTop: "24px" }}>
        {title}
      </Typography>
      {isMobile ? (
        // On mobile: use Swiper with 1.3 slides per view and custom pagination
        <MobileSwiper
          slidesPerView={1.3}
          spaceBetween={24}
          pagination={{ clickable: true }}
          modules={[Pagination]}
          style={{ paddingTop: "64px", width: "100%" }}
        >
          {itemsList.map((item: any, index: number) => (
            <SwiperSlide key={index}>
              <RecommendationItem item={item} />
            </SwiperSlide>
          ))}
        </MobileSwiper>
      ) : itemsList.length >= 4 ? (
        // On desktop: use auto-scroll with duplicated items for an endless loop
        <AutoScrollDiv>
          <div className="scrolling-content">
            {duplicatedItems.map((item: any, index: number) => (
              <RecommendationItem item={item} key={index} />
            ))}
          </div>
        </AutoScrollDiv>
      ) : (
        // If fewer than 4 cards on desktop, show them in a regular flex container.
        <StyledItemsDiv>
          {itemsList.map((item: any, index: number) => (
            <RecommendationItem item={item} key={index} />
          ))}
        </StyledItemsDiv>
      )}
    </StyledDiv>
  )
}

export default Recomendations
